import { useRouteError } from "react-router";
import { Button, Stack, Box, Card, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import oops from "../assets/oops.png";

interface ErrorProps {
    permissionError?: boolean;
    notFound?: boolean;
}

const Error = ({ permissionError, notFound }: ErrorProps) => {
    const error: any = useRouteError();

    const jwt: any = localStorage.getItem("token");
    const labUserCheck: any = jwt
        ? JSON.parse(atob(jwt.split(".")[1]))
        : null;

    const handleNavigation = () => {
        if (permissionError) {
            localStorage.clear();
            window.location.replace("/");
            return;
        }

        const isAuthenticated = labUserCheck?.ValidOTP === "True";

        const isExternal = labUserCheck?.UserType === "External";
        const hasPermission = isExternal || !!labUserCheck?.Permission;

        if (isAuthenticated && hasPermission) {
            window.location.replace("/home");
        } else {
            localStorage.clear();
            window.location.replace("/");
        }
    };

    const renderPage = (
        title: string,
        message: React.ReactNode,
        buttonText = "Back To Home"
    ) => (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                    "linear-gradient(135deg, #f5f7fb 0%, #eef2f7 100%)",
                p: 2,
            }}
        >
            <Card
                elevation={4}
                sx={{
                    maxWidth: 450,
                    width: "100%",
                    textAlign: "center",
                    borderRadius: 4,
                    p: 4,
                }}
            >
                <Box
                    sx={{
                        width: 100,
                        height: 100,
                        mx: "auto",
                        mb: 3,
                        borderRadius: "50%",
                        backgroundColor: "#fff3f3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <img src={oops} alt="oops" width={75} />
                </Box>

                <Typography variant="h5" fontWeight={600} mb={2}>
                    {title}
                </Typography>

                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    {message}
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNavigation}
                    sx={{
                        borderRadius: 3,
                        px: 4,
                        textTransform: "none",
                    }}
                >
                    {buttonText}
                </Button>
            </Card>
        </Box>
    );

    // 1. Unknown route should always show 404
    if (notFound || error?.status === 404) {
        return renderPage(
            "404 - Page Not Found",
            "Sorry, the page you are looking for could not be found."
        );
    }

    // 2. Permission check
    if (permissionError) {
        return renderPage(
            "",
            <>
                You don't have permission to access the application.
                <br />
                Please contact <b>Tony.Wong@eurofinsanz.com</b> to have the
                required role assigned to your account, then try again.
            </>,
            "Go Back"
        );
    }

    // 3. Other errors
    return renderPage(
        "Something Went Wrong",
        error?.message || "An unexpected error occurred."
    );
};

export default Error;